import type { FC } from 'react';
import { lazy, useMemo } from 'react';
import { useAuctionTiles } from 'src/apis/monolith/use-auction-tiles';
import { useFavouriteNetworkPointsMonolith } from 'src/apis/monolith/use-favourite-network-points';
import {
  type MyUserDetailsRequest,
  useMyUserDetails,
} from 'src/apis/organisation-service/use-my-user-details';
import {
  type OptionalOrganisationDetailsRequest,
  useOptionalOrganisationDetails,
} from 'src/apis/organisation-service/use-organisation-details';
import { useOrganisationService } from 'src/apis/organisation-service/use-organisation-service';
import { useOrganisationServicePublic } from 'src/apis/organisation-service/use-organisation-service-public';
import {
  type PublicOperatorsRequest,
  usePublicOperators,
} from 'src/apis/organisation-service/use-public-operators';
import { useAuctionTiles as useAuctionTilesTAS } from 'src/apis/transport-auction/use-auction-tiles';
import { useOptionalTransportAuctionServiceRoot } from 'src/apis/transport-auction/use-transport-auction-service-root';
import { useOptionalTtpcModulationService } from 'src/apis/ttpc-modulation-service/use-ttpc-modulation-service';
import { PageSpinner } from 'src/components/spinner-container';
import type { Successful } from 'src/hooks/use-axios';
import { useOptionalAuthenticatedMonolithUser } from 'src/hooks/use-monolith-user';
import {
  useDashboardPageParams,
  type DashboardPageParams,
} from 'src/pages/dashboard/use-page-params';
import { useBidCollectorRoot } from 'src/pages/snam-bid-collector/use-bid-collector';

const Page = lazy(() => import('./page'));

export const Dashboard: FC = () => {
  const organisationServicePublic = useOrganisationServicePublic();
  const operators = usePublicOperators({
    randomOrder: true,
    organisationServicePublic,
  });

  const organisationService = useOrganisationService();
  const myUserDetails = useMyUserDetails({ organisationService });
  const myOrganisationDetails = useOptionalOrganisationDetails({
    organisationService,
  });

  const monolithUser = useOptionalAuthenticatedMonolithUser();

  const pageParams = useDashboardPageParams();

  if (
    !operators.response ||
    !myUserDetails.response ||
    !myOrganisationDetails?.response
  )
    return <PageSpinner />;

  if (monolithUser?.isShipper) {
    return (
      <LoadShipperData
        operators={operators}
        myUserDetails={myUserDetails}
        myOrganisationDetails={myOrganisationDetails}
        pageParams={pageParams}
      />
    );
  }

  return (
    <Page
      operators={operators}
      myUserDetails={myUserDetails}
      myOrganisationDetails={myOrganisationDetails}
      pageParams={pageParams}
    />
  );
};

type LoadShipperDataProps = {
  operators: Successful<PublicOperatorsRequest>;
  myUserDetails: Successful<MyUserDetailsRequest>;
  myOrganisationDetails: Successful<OptionalOrganisationDetailsRequest>;
  pageParams: DashboardPageParams;
};

const LoadShipperData: FC<LoadShipperDataProps> = ({
  operators,
  myUserDetails,
  myOrganisationDetails,
  pageParams,
}) => {
  const auctionTiles = useAuctionTiles();

  const transportAuctionServiceRoot = useOptionalTransportAuctionServiceRoot();
  const auctionTilesTAS = useAuctionTilesTAS({
    root: transportAuctionServiceRoot,
  });

  const snamBidCollector = useBidCollectorRoot();

  const ttpcModulationService = useOptionalTtpcModulationService();

  const favouriteNetworkPointsParams = useMemo(
    () => ({
      pageSize: pageParams.favouriteNetworkPoints.value.pageSize,
      start: pageParams.favouriteNetworkPoints.value.start,
    }),
    [
      pageParams.favouriteNetworkPoints.value.pageSize,
      pageParams.favouriteNetworkPoints.value.start,
    ]
  );
  const favouriteNetworkPoints = useFavouriteNetworkPointsMonolith(
    favouriteNetworkPointsParams
  );

  if (
    transportAuctionServiceRoot?.loaded === false ||
    auctionTilesTAS?.response === null ||
    !auctionTiles.response ||
    !snamBidCollector.response ||
    (PRISMA_CONFIG.ttpcModulationService && !ttpcModulationService.loaded) ||
    !favouriteNetworkPoints.response
  )
    return <PageSpinner />;

  return (
    <Page
      operators={operators}
      myUserDetails={myUserDetails}
      myOrganisationDetails={myOrganisationDetails}
      auctionTiles={auctionTiles}
      auctionTilesTAS={auctionTilesTAS}
      snamBidCollector={snamBidCollector}
      ttpcModulationService={ttpcModulationService}
      favouriteNetworkPoints={favouriteNetworkPoints}
      pageParams={pageParams}
    />
  );
};
